import React, { useState } from "react";
import { Navigate, useLocation, useNavigate, Link } from "react-router-dom";
import { Loader2, LogIn, Anchor } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

export const AdminLoginPage = () => {
  const { admin, login, loading } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const from = location.state?.from || "/beheer";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F8FB]">
        <Loader2 className="w-5 h-5 animate-spin text-[#2E9CD6]" />
      </div>
    );
  }
  if (admin) return <Navigate to={from} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Vul e-mailadres en wachtwoord in.");
      return;
    }
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(from, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Inloggen mislukt. Controleer je gegevens.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-[#F4F8FB] flex items-center justify-center p-4"
      style={{
        backgroundImage:
          "radial-gradient(at 20% 0%, rgba(107,192,232,0.25), transparent 50%), radial-gradient(at 80% 100%, rgba(46,156,214,0.18), transparent 45%)",
      }}
    >
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="flex items-center justify-center gap-1 mb-8 font-[Poppins] leading-none"
        >
          <span className="text-3xl font-extrabold tracking-tight text-[#173A5E]">
            LEMMER
          </span>
          <span className="text-xl font-semibold text-[#2E9CD6] italic">
            Leeft
          </span>
        </Link>

        <div className="bg-white rounded-2xl shadow-xl border border-[#E4EEF5] p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-[#173A5E] flex items-center justify-center">
              <Anchor className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs font-[Poppins] font-semibold uppercase tracking-wider text-[#5A7594]">
                Beheer
              </p>
              <h1 className="text-2xl font-bold text-[#173A5E] leading-tight">
                Log in
              </h1>
            </div>
          </div>

          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-[Poppins] font-semibold text-[#173A5E] mb-1.5"
              >
                E-mailadres
              </label>
              <input
                id="email"
                type="email"
                data-testid="admin-login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                placeholder="beheer@lemmer-leeft.nl"
                className="w-full bg-white border border-[#E4EEF5] rounded-xl px-4 py-2.5 font-[Inter] text-[15px] text-[#173A5E] outline-none focus:border-[#2E9CD6] focus:ring-2 focus:ring-[#6BC0E8]/40 transition"
              />
            </div>
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-[Poppins] font-semibold text-[#173A5E] mb-1.5"
              >
                Wachtwoord
              </label>
              <input
                id="password"
                type="password"
                data-testid="admin-login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="••••••••"
                className="w-full bg-white border border-[#E4EEF5] rounded-xl px-4 py-2.5 font-[Inter] text-[15px] text-[#173A5E] outline-none focus:border-[#2E9CD6] focus:ring-2 focus:ring-[#6BC0E8]/40 transition"
              />
            </div>

            {error && (
              <div
                data-testid="admin-login-error"
                className="p-3 rounded-xl bg-[#FDECEE] border border-[#F5C3C8] text-[#8b1420] text-sm font-[Inter]"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="ll-btn-primary w-full disabled:opacity-60 disabled:cursor-wait"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <LogIn className="w-4 h-4" />
              )}
              Inloggen
            </button>

            <div className="text-center pt-1">
              <Link
                to="/beheer/wachtwoord-vergeten"
                data-testid="admin-forgot-password-link"
                className="text-sm font-[Poppins] font-semibold text-[#2E9CD6] hover:text-[#173A5E] transition"
              >
                Wachtwoord vergeten?
              </Link>
            </div>
          </form>

          <p className="mt-6 text-xs text-center text-[#5A7594] font-[Inter]">
            Alleen voor beheerders van Lemmer Leeft. Vragen? Neem contact op met
            BIZ Vereniging Lemmer.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
